import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ContractsService } from "../../../services/contract/contracts.ts";
import {
  Contract,
  convertContractToCloned,
  convertContractToCreate,
  convertContractToUpdate,
  defaultContract,
  validate,
} from "../../../Models/Contract.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { useAppContext } from "../../../AppProvider.jsx";
import {
  callErrorToast,
  callSuccessToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities.ts";

const contractsService = new ContractsService();

interface HandlerContractProviderProps {
  id?: string;
  children: ReactNode;
  callback?: () => void;
  autosave: boolean;
  action?: string;
}

interface HandlerContractContextValue {
  data: Partial<Contract>;
  error: ErrorResponse<Contract> | null;
  loader: boolean;
  callback?: () => void;
  edit: (contract: Contract, property: keyof Contract, save?: boolean) => void;
  clone: (contract: Contract) => void;
  onSubmit: (id?: string) => void;
}

const HandlerContractContext = createContext<
  HandlerContractContextValue | undefined
>(undefined);

const HandlerContractProvider: React.FC<HandlerContractProviderProps> = ({
  id,
  children,
  autosave,
  callback,
  action,
}) => {
  const { dictionary } = useAppContext();
  const [data, setData] = useState<Partial<Contract>>(defaultContract);
  const [error, setError] = useState<ErrorResponse<Contract> | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const prevError = useRef<ErrorResponse<Contract>>(null);

  const get = (id: string): void => {
    setLoader(true);
    contractsService
      .get(id)
      .then((res) => {
        setData(res.data);
        setLoader(false);
      })
      .catch((err) => {
        callErrorToast(err);
        setLoader(false);
        setError(err);
      });
  };

  const create = (contract: Contract): void => {
    const validation = validate(data as Contract);
    if (!validation.isValid) {
      setError({ response: { data: validation.errors } });
      setLoader(false);
      return;
    }
    const contractToCreate = convertContractToCreate(contract);

    contractsService
      .create(contractToCreate)
      .then((res) => {
        setData(res.data);
        setLoader(false);
        callSuccessToast(dictionary.messages.save_successful);
        if (callback) callback();
      })
      .catch((err) => {
        setError(err);
        callErrorToast(err);
        setLoader(false);
      });
  };

  const edit = (
    contract: Contract,
    property: keyof Contract | null,
    save = true
  ): void => {
    contract = cleanObject(contract);
    if (property) {
      removeError(property, error, prevError, setError);
    }

    if (save && autosave) {
      const validation = validate(data as Contract);
      if (!validation.isValid) {
        setError({ response: { data: validation.errors } });
        setLoader(false);
        return;
      }

      const contractUpdated = convertContractToUpdate(contract);

      contractsService
        .edit(contractUpdated)
        .then(() => {
          setData(contract);
          if (save && callback) {
            callback();
          }
          callSuccessToast(dictionary.messages.save_successful);
        })
        .catch((err) => {
          setError(err);
          callErrorToast(err);
          setLoader(false);
        });
      return;
    }
    setData(contract);
  };

  const clone = (contract: Contract): void => {
    setLoader(true);

    const validation = validate(data as Contract);
    if (!validation.isValid) {
      setError({ response: { data: validation.errors } });
      callErrorToast(dictionary.messages.validation_error);
      setLoader(false);
      return;
    }

    const contractCloned = convertContractToCloned(contract);

    contractsService
      .create(contractCloned)
      .then((res) => {
        setData(res.data);
        setLoader(false);
        callSuccessToast(dictionary.messages.save_successful);
        if (callback) callback();
      })
      .catch((err) => {
        setError(err);
        callErrorToast(err);
        setLoader(false);
      });
  };

  const onSubmit = (): void => {
    setLoader(true);
    if (action === "clone") {
      clone(data as Contract);
      return;
    }
    if (action === "edit") {
      edit(data as Contract, null);
      return;
    }
    create(data as Contract);
  };

  useEffect(() => {
    if (id) {
      get(id);
    }
  }, [id]);

  return (
    <HandlerContractContext.Provider
      value={{
        data,
        error,
        loader,
        edit,
        onSubmit,
        callback,
        clone,
      }}
    >
      {children}
    </HandlerContractContext.Provider>
  );
};

const useHandlerContext = (): HandlerContractContextValue => {
  const context = useContext(HandlerContractContext);
  if (!context) {
    throw new Error(
      "useHandlerContext must be used within a HandlerContractProvider"
    );
  }
  return context;
};

export { HandlerContractProvider, useHandlerContext };
